import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { PageHeader, CandidateAvatar, StatusBadge, EmptyState } from "@/components/Common";
import { api } from "@/lib/api";

const ROWS = [
  ["Hourly rate", (c) => `$${c.hourly_rate}/hr`, "min", (c) => c.hourly_rate],
  ["Experience", (c) => `${c.experience_years} years`, "max", (c) => c.experience_years],
  ["Category", (c) => c.category],
  ["Availability", (c) => c.availability],
  ["Status", (c) => <StatusBadge status={c.status} />],
  ["Location", (c) => c.location],
  ["Timezone", (c) => c.timezone],
  ["English", (c) => c.english_level],
  ["Languages", (c) => (c.languages || []).join(", ")],
  ["Verified licenses", (c) => (c.licenses || []).filter((l) => l.verified).length, "max", (c) => (c.licenses || []).filter((l) => l.verified).length],
  ["Skills", (c) => <div className="flex flex-wrap gap-1">{(c.skills || []).map((s) => <span key={s} className="rounded-sm bg-stone-100 px-1.5 py-0.5 text-xs">{s}</span>)}</div>],
  ["Tools", (c) => (c.tools || []).join(", ")],
  ["Latest role", (c) => c.work_history?.[0] ? `${c.work_history[0].role} · ${c.work_history[0].company}` : "—"],
  ["Education", (c) => c.education?.[0]?.degree || "—"],
];

export default function Compare() {
  const [params] = useSearchParams();
  const [list, setList] = useState(null);
  useEffect(() => {
    const ids = (params.get("ids") || "").split(",").filter(Boolean);
    Promise.all(ids.map((id) => api.get(`/candidates/${id}`).then((r) => r.data).catch(() => null))).then((r) => setList(r.filter(Boolean)));
  }, [params]);

  if (list && list.length < 2) return <EmptyState testId="compare-empty" title="Pick at least two candidates" desc="Select candidates on your shortlist to compare them."><Link to="/app/shortlist" className="text-sm font-medium underline">Go to shortlist</Link></EmptyState>;

  const best = (fn, kind) => {
    const vals = (list || []).map(fn);
    return kind === "min" ? Math.min(...vals) : Math.max(...vals);
  };

  return (
    <div>
      <PageHeader eyebrow="Side by side" title="Compare candidates" desc="Highlighted cells show the strongest value in each row." />
      <div className="overflow-x-auto rounded-md border border-stone-200 bg-white" data-testid="compare-table">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-stone-200">
              <th className="w-40 p-5" />
              {(list || []).map((c) => (
                <th key={c.id} className="p-5 text-left align-top" data-testid={`compare-col-${c.id}`}>
                  <CandidateAvatar candidate={c} className="h-16 w-16" />
                  <Link to={`/app/candidates/${c.id}`} className="mt-3 block font-display text-lg font-bold text-stone-900 hover:underline">{c.name}</Link>
                  <p className="text-xs font-normal text-stone-500">{c.title}</p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {ROWS.map(([label, render, kind, num]) => {
              const b = kind && list ? best(num, kind) : null;
              return (
                <tr key={label}>
                  <td className="p-4 text-[11px] font-mono uppercase tracking-wider text-stone-500">{label}</td>
                  {(list || []).map((c) => (
                    <td key={c.id} className={`p-4 text-stone-800 ${kind && num(c) === b ? "bg-emerald-50 font-semibold text-emerald-800" : ""}`}>
                      {render(c)}{kind && num(c) === b && label === "Verified licenses" && b > 0 && <ShieldCheck className="ml-1 inline h-4 w-4" />}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
